import { FilterRouter, SORT_TYPES } from '@/utils/filterRouter'
import { pipe } from '@/utils/functionalBob'

const validateView = (supportedViews, defaultView) => ({ route, valid, newRouteObj }) => {
  const view = FilterRouter.extractView(route)
  if (!supportedViews.includes(view)) {
    valid = false
    newRouteObj = FilterRouter.updateRoute(newRouteObj, FilterRouter.applyView(defaultView))
  }
  return { route, valid, newRouteObj }
}

const validateOption = (supportedOptions, defaultOption) => ({ route, valid, newRouteObj }) => {
  const option = FilterRouter.extractOption(route)
  if (!supportedOptions.includes(option)) {
    valid = false
    newRouteObj = FilterRouter.updateRoute(newRouteObj, FilterRouter.applyOption(defaultOption))
  }
  return { route, valid, newRouteObj }
}

const validatePaging = (defaultPaging) => ({ route, valid, newRouteObj }) => {
  const { page, size } = FilterRouter.extractPaging(route)
  if (isNaN(parseInt(page)) || isNaN(parseInt(size))) {
    valid = false
    newRouteObj = FilterRouter.updateRoute(newRouteObj, FilterRouter.applyPaging(defaultPaging))
  }
  return { route, valid, newRouteObj }
}

const validateFilters = (supportedFilterTypes) => ({ route, valid, newRouteObj }) => {
  const filters = FilterRouter.extractFilters(route)
  const supportedFilters = filters.filter(f => supportedFilterTypes.includes(f.filterType))
  if (filters.length !== supportedFilters.length) {
    valid = false
    newRouteObj = FilterRouter.updateRoute(newRouteObj, FilterRouter.applyFilters(supportedFilters))
  }
  return { route, valid, newRouteObj }
}

const validateSorting = (sortableFields, defaultSorting) => ({ route, valid, newRouteObj }) => {
  const { sortKey } = FilterRouter.extractSortBy(route)
  if (!sortableFields.includes(sortKey)) {
    valid = false
    newRouteObj = FilterRouter.updateRoute(newRouteObj, FilterRouter.applySortBy(defaultSorting))
  }
  return { route, valid, newRouteObj }
}

const validator = (funcs) => {
  const validationPipe = pipe(...funcs)
  return (routeToValidate) => {
    const { route, valid, newRouteObj } = validationPipe({ route: routeToValidate, valid: true, newRouteObj: {} })
    return {
      route: FilterRouter.updateRoute(route, newRouteObj),
      valid
    }
  }
}

export class RouteValidatorBuilder {
  constructor () {
    this.defaultView = null
    this.supportedViews = []
    this.supportedOptions = []
    this.defaultOption = null

    this.defaultPageNumber = null
    this.defaultPageSize = null

    this.supportedFilterTypes = []

    this.sortableFields = []
    this.defaultSortKey = null
    this.defaultSortOrder = null
  }

  withDefaultView (defaultView) {
    this.defaultView = defaultView
    return this
  }
  withDefaultOption (defaultOption) {
    this.defaultOption = defaultOption
    return this
  }
  withSupportedViews (supportedViews) {
    this.supportedViews = supportedViews
    return this
  }
  withSupportedOptions (supportedOptions) {
    this.supportedOptions = supportedOptions
    return this
  }

  withDefaultPageNumber (num) {
    this.defaultPageNumber = num
    return this
  }
  withDefaultPageSize (size) {
    this.defaultPageSize = size
    return this
  }

  withSupportedFilterTypes (filterTypes) {
    this.supportedFilterTypes = filterTypes
    return this
  }

  withSortableFields (sortableFields) {
    this.sortableFields = sortableFields
    return this
  }
  withDefaultSortKey (defaultSortKey) {
    this.defaultSortKey = defaultSortKey
    return this
  }
  withDefaultSortOrder (defaultSortOrder) {
    this.defaultSortOrder = defaultSortOrder
    return this
  }

  toValidator () {
    const validationFuncs = []

    const sufficientInfoForValidateViews = this.supportedViews.length > 0 && this.defaultView !== null && this.supportedViews.includes(this.defaultView)
    if (sufficientInfoForValidateViews) validationFuncs.push(validateView(this.supportedViews, this.defaultView))

    const sufficientInfoForValidateOptions = this.supportedOptions.length > 0 && this.defaultOption !== null && this.supportedOptions.includes(this.defaultOption)
    if (sufficientInfoForValidateOptions) validationFuncs.push(validateOption(this.supportedOptions, this.defaultOption))

    const sufficientInfoForValidatePaging = this.defaultPageNumber !== null && this.defaultPageSize !== null
    if (sufficientInfoForValidatePaging) validationFuncs.push(validatePaging({ page: this.defaultPageNumber, size: this.defaultPageSize }))

    const sufficientInfoForValidateFilters = this.supportedFilterTypes.length > 0
    if (sufficientInfoForValidateFilters) validationFuncs.push(validateFilters(this.supportedFilterTypes))

    const sufficientInfoForValidateSorting = this.sortableFields.length > 0 && this.defaultSortKey !== null && this.defaultSortOrder !== null && Object.values(SORT_TYPES).includes(this.defaultSortOrder)
    if (sufficientInfoForValidateSorting) validationFuncs.push(validateSorting(this.sortableFields, { sortKey: this.defaultSortKey, descending: this.defaultSortOrder === SORT_TYPES.DESCENDING }))

    return validator(validationFuncs)
  }
}
